/**
 * Per-level completion + best-replay store.
 *
 * Tracks which built-in levels the user has finished, the best (lowest)
 * frame count, and the encoded replay that achieved it. Used by the
 * level-select modal to render a "completed" badge and a "watch best
 * replay" affordance, and by the win flow to opportunistically save the
 * run when it beats the prior best (or is the first completion).
 *
 * Storage: localStorage key `shk.progress.v1`, JSON-encoded
 * `{ [levelId]: LevelRecord }`. Replays are stored as the same SHKR1
 * text format used by Save Replay / Load Replay, so they can round-trip
 * through the existing parseReplay code path without a separate decoder.
 */

import { encodeReplay, parseReplay, type Replay } from './Replay.js';

export interface LevelRecord {
  levelId: string;
  /** Best (lowest) frame count for this level. */
  bestFrames: number;
  /** SHKR1-encoded replay string for the best run. */
  replay: string;
  /** Epoch ms of when the best was achieved. */
  completedAt: number;
}

const KEY = 'shk.progress.v1';
/** Separate key so the last-played pointer can't corrupt the completion
 *  store and vice-versa. Stores a single builtin level id string. */
const LAST_PLAYED_KEY = 'shk.lastPlayed.v1';

type Store = Record<string, LevelRecord>;

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Store;
  } catch { return {}; }
}

function write(s: Store): void {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore quota */ }
}

/** Lookup the saved record for a level, or null if never completed. */
export function getRecord(levelId: string): LevelRecord | null {
  const r = read()[levelId];
  return r ?? null;
}

/** Cheap check used to render the badge in the level-select modal. */
export function isCompleted(levelId: string): boolean {
  return getRecord(levelId) !== null;
}

/**
 * Record a successful run. Writes the new best when (a) no prior record
 * exists, or (b) `frames` is strictly less than the saved best.
 * Returns true when the store was updated (so the caller can decide
 * whether to show a "new best!" flourish).
 */
export function recordCompletion(replay: Replay): boolean {
  if (replay.result !== 'won') return false;
  const all = read();
  const prior = all[replay.levelId];
  if (prior && prior.bestFrames <= replay.frames) return false;
  all[replay.levelId] = {
    levelId: replay.levelId,
    bestFrames: replay.frames,
    replay: encodeReplay(replay),
    completedAt: Date.now(),
  };
  write(all);
  return true;
}

/** Decode the saved best replay for a level, or null if missing/corrupt. */
export function loadBestReplay(levelId: string): Replay | null {
  const rec = getRecord(levelId);
  if (!rec) return null;
  return parseReplay(rec.replay);
}

/**
 * Pick the level the player should resume on at app start: the last
 * builtin they actually played (recorded via `recordLastPlayed`). Falls
 * back to the next-after-highest-completed heuristic when no last-played
 * pointer is set (fresh install or user level / replay session).
 * If none are completed returns 0; if every level is completed returns
 * the last index so the user lands on the final level instead of looping
 * back.
 */
export function getResumeIndex(orderedIds: string[]): number {
  if (orderedIds.length === 0) return 0;
  const last = getLastPlayedId();
  if (last) {
    const idx = orderedIds.indexOf(last);
    if (idx >= 0) return idx;
  }
  const all = read();
  let lastCompleted = -1;
  for (let i = 0; i < orderedIds.length; i++) {
    if (orderedIds[i]! in all) lastCompleted = i;
  }
  if (lastCompleted < 0) return 0;
  return Math.min(lastCompleted + 1, orderedIds.length - 1);
}

/** Read the saved last-played builtin id, or null if none. */
export function getLastPlayedId(): string | null {
  try {
    const raw = localStorage.getItem(LAST_PLAYED_KEY);
    return raw && typeof raw === 'string' ? raw : null;
  } catch { return null; }
}

/** Remember which builtin level the user is currently on, so the next
 *  app launch resumes there. Idempotent; safe to call every level load. */
export function recordLastPlayed(levelId: string): void {
  try { localStorage.setItem(LAST_PLAYED_KEY, levelId); } catch { /* ignore quota */ }
}

/** Wipe a single level's record. Used by the "clear progress" affordance
 *  if/when one is added. Idempotent. */
export function clearRecord(levelId: string): void {
  const all = read();
  if (!(levelId in all)) return;
  delete all[levelId];
  write(all);
}
